import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ChevronRight, Eye, Loader2, Store, Globe, ShieldCheck, Clock, X } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const STEPS = ["Business Details", "Choose Theme", "Customize", "Checkout", "Confirmation"];

export default function SiteAffiliateFlow({ kind }) {
  const [params] = useSearchParams();
  const ref = (params.get("ref") || "").toUpperCase();
  const [cfg, setCfg] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(() => { try { return localStorage.getItem(`sa_order_${kind}`) || ""; } catch { return ""; } });
  const [order, setOrder] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", category: "", password: "" });
  const [custom, setCustom] = useState({ display_name: "", tagline: "", primary_color: "#007A4D", contact_email: "", contact_phone: "", address: "" });
  const [pay, setPay] = useState({ reference: "", paid_at: "", note: "" });

  useEffect(() => {
    api.get("/site-affiliate/config", { params: { kind, ref } }).then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e)));
    try {
      if (!sessionStorage.getItem(`sa_trk_${kind}_${ref}`) && ref) {
        sessionStorage.setItem(`sa_trk_${kind}_${ref}`, "1");
        api.post("/site-affiliate/track", { kind, ref }).catch(() => {});
      }
    } catch { /* ignore */ }
  }, [kind, ref]);

  useEffect(() => {
    if (!token) return;
    api.get(`/site-affiliate/orders/${token}`).then((r) => {
      setOrder(r.data.order);
      if (["payment_pending", "approved", "rejected"].includes(r.data.order.status)) setStep(4);
    }).catch(() => { try { localStorage.removeItem(`sa_order_${kind}`); } catch { /* ignore */ } setToken(""); });
  }, [token, kind]);

  const F = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  const C = (k) => ({ value: custom[k], onChange: (e) => setCustom({ ...custom, [k]: e.target.value }) });

  const submitDetails = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/site-affiliate/orders", { kind, ref, customer: form });
      try { localStorage.setItem(`sa_order_${kind}`, data.token); } catch { /* ignore */ }
      setToken(data.token); setOrder({ status: "building", price: data.price }); setStep(1);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const pickTheme = async (tid) => {
    setBusy(true);
    try { await api.post(`/site-affiliate/orders/${token}/theme`, { theme_id: tid }); setOrder({ ...order, theme_id: tid }); setStep(2); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveCustom = async () => {
    setBusy(true);
    try { await api.post(`/site-affiliate/orders/${token}/customize`, custom); setStep(3); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitPayment = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/site-affiliate/orders/${token}/payment`, pay);
      setOrder({ ...order, status: "payment_pending" }); setStep(4);
      toast.success("Payment submitted for verification");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const isStore = kind === "store";
  const Icon = isStore ? Store : Globe;
  if (!cfg) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;
  if (!cfg.enabled) return <div className="min-h-screen grid place-items-center p-6 text-center"><p className="font-display font-bold text-lg">Direct {isStore ? "store" : "website"} creation is currently unavailable.</p></div>;

  return (
    <div className="min-h-screen bg-[#f6f8f7]" data-testid="site-affiliate-flow">
      <header className="border-b bg-white">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-lg tracking-tight">MILTROO</Link>
          {cfg.ref.valid && <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full" data-testid="sa-ref-badge">Referred by {cfg.ref.creator_name}</span>}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center gap-1.5 mb-8 overflow-x-auto pb-1" data-testid="sa-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1.5 shrink-0">
              <span className={`h-7 w-7 rounded-full text-[11px] font-extrabold flex items-center justify-center ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-emerald-100 text-emerald-700 border-2 border-emerald-600" : "bg-muted text-muted-foreground"}`}>
                {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </span>
              <span className={`text-xs font-bold ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
              {i < STEPS.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground mx-1" />}
            </div>
          ))}
        </div>

        <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-border rounded-3xl p-6 sm:p-8 shadow-sm">
          {step === 0 && (
            <div className="space-y-5" data-testid="sa-step-details">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}><Icon className="h-5 w-5" /></div>
                <div>
                  <h1 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight">Create your Website / Online Store</h1>
                  <p className="text-sm text-muted-foreground">One product — business website with full online store. No partner needed, build directly on MILTROO.</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Business Name *</Label><Input data-testid="sa-business-name" {...F("business_name")} /></div>
                <div className="space-y-1.5"><Label>Owner Name *</Label><Input data-testid="sa-owner-name" {...F("name")} /></div>
                <div className="space-y-1.5"><Label>Mobile Number *</Label><Input data-testid="sa-phone" {...F("phone")} /></div>
                <div className="space-y-1.5"><Label>Email *</Label><Input data-testid="sa-email" type="email" {...F("email")} /></div>
                <div className="space-y-1.5"><Label>Business Category *</Label><Input data-testid="sa-category" placeholder="Fashion, Food, Services…" {...F("category")} /></div>
                <div className="space-y-1.5"><Label>Dashboard Password *</Label><Input data-testid="sa-password" type="password" placeholder="min 6 characters" {...F("password")} /></div>
              </div>
              <Button data-testid="sa-details-next" onClick={submitDetails} disabled={busy} className="rounded-full font-bold text-white border-0 px-8" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue — {fmtINR(cfg.price)} <ChevronRight className="h-4 w-4 ml-1" /></>}
              </Button>
            </div>
          )}

          {step === 1 && (
            <div data-testid="sa-step-theme">
              <h2 className="font-display font-extrabold text-xl mb-1">Choose a theme</h2>
              <p className="text-sm text-muted-foreground mb-5">Preview any theme before selecting.</p>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {(cfg.themes || []).map((t) => (
                  <div key={t.id} className="border border-border rounded-2xl overflow-hidden hover:border-emerald-500 transition-colors" data-testid={`sa-theme-${t.id}`}>
                    <div className="aspect-[4/3] bg-muted relative">
                      {t.preview && <img src={t.preview} alt={t.name} className="w-full h-full object-cover" />}
                      <button onClick={() => setPreview(t)} data-testid={`sa-preview-${t.id}`}
                        className="absolute top-2 right-2 h-8 px-3 rounded-full bg-black/60 text-white text-[11px] font-bold flex items-center gap-1"><Eye className="h-3.5 w-3.5" />Preview</button>
                    </div>
                    <div className="p-3 flex items-center justify-between gap-2">
                      <div><p className="font-bold text-sm">{t.name}</p><p className="text-[11px] text-muted-foreground capitalize">{t.category}</p></div>
                      <Button size="sm" data-testid={`sa-pick-${t.id}`} disabled={busy} onClick={() => pickTheme(t.id)} className="rounded-full font-bold text-white border-0" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}>Select</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="sa-step-customize">
              <h2 className="font-display font-extrabold text-xl">Customize your {isStore ? "store" : "website"}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Display Name</Label><Input data-testid="sa-cust-name" placeholder={form.business_name} {...C("display_name")} /></div>
                <div className="space-y-1.5"><Label>Tagline</Label><Input data-testid="sa-cust-tagline" {...C("tagline")} /></div>
                <div className="space-y-1.5"><Label>Brand Color</Label><Input data-testid="sa-cust-color" type="color" {...C("primary_color")} className="h-10 p-1" /></div>
                <div className="space-y-1.5"><Label>Contact Phone</Label><Input data-testid="sa-cust-phone" {...C("contact_phone")} /></div>
                <div className="space-y-1.5 sm:col-span-2"><Label>Address</Label><Input data-testid="sa-cust-address" {...C("address")} /></div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-full font-bold">Back</Button>
                <Button data-testid="sa-custom-next" onClick={saveCustom} disabled={busy} className="rounded-full font-bold text-white border-0" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue to Checkout"}
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="sa-step-checkout">
              <h2 className="font-display font-extrabold text-xl">Checkout</h2>
              <div className="rounded-2xl border border-border p-5 space-y-2" data-testid="sa-price-summary">
                <div className="flex justify-between text-sm"><span>Website / Store Creation (1 year)</span><b>{fmtINR(cfg.price)}</b></div>
                <div className="flex justify-between text-sm text-muted-foreground"><span>Hosting · SSL · Builder access</span><span>Included</span></div>
                <div className="border-t pt-2 flex justify-between font-extrabold"><span>Total</span><span data-testid="sa-total">{fmtINR(cfg.price)}</span></div>
              </div>
              {cfg.payment_link ? (
                <a href={cfg.payment_link} target="_blank" rel="noreferrer" data-testid="sa-pay-link"
                  className="inline-flex h-11 px-6 rounded-full text-white font-bold items-center" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}>
                  Pay {fmtINR(cfg.price)} securely →
                </a>
              ) : <p className="text-sm text-amber-600 font-semibold">Payment link is being configured — please check back shortly.</p>}
              {cfg.payment_instructions && <p className="text-xs text-muted-foreground whitespace-pre-line">{cfg.payment_instructions}</p>}
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-bold">After paying, submit your payment reference:</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Transaction / UTR / Reference *</Label><Input data-testid="sa-pay-ref" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Payment Date</Label><Input data-testid="sa-pay-date" type="date" value={pay.paid_at} onChange={(e) => setPay({ ...pay, paid_at: e.target.value })} /></div>
                </div>
                <Button data-testid="sa-pay-submit" onClick={submitPayment} disabled={busy || !pay.reference.trim()} className="rounded-full font-bold text-white border-0" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for Verification"}
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="text-center py-6 space-y-4" data-testid="sa-step-done">
              {order?.status === "approved" ? (
                <><div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/15 flex items-center justify-center"><CheckCircle2 className="h-8 w-8 text-emerald-600" /></div>
                  <h2 className="font-display font-extrabold text-2xl">Payment Approved!</h2>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">Congratulations! Your payment has been verified and approved. Your MILTROO Business Owner account is ready — log in with your email and dashboard password to continue building.</p>
                  <Link to="/login" data-testid="sa-goto-login" className="inline-flex h-11 px-8 rounded-full text-white font-bold items-center" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}>Go to Login</Link></>
              ) : order?.status === "rejected" ? (
                <><div className="mx-auto h-16 w-16 rounded-full bg-red-500/15 flex items-center justify-center"><X className="h-8 w-8 text-red-500" /></div>
                  <h2 className="font-display font-extrabold text-2xl">Payment Not Verified</h2>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">{order?.admin_note || "Your payment could not be verified. Please contact support."}</p></>
              ) : (
                <><div className="mx-auto h-16 w-16 rounded-full bg-amber-500/15 flex items-center justify-center"><Clock className="h-8 w-8 text-amber-500" /></div>
                  <h2 className="font-display font-extrabold text-2xl" data-testid="sa-pending-title">Payment Verification Pending</h2>
                  <p className="text-sm text-muted-foreground max-w-lg mx-auto" data-testid="sa-pending-msg">{cfg.verification_message || "Congratulations! Thank you for choosing MILTROO for your business website. Your payment is awaiting verification. Our team will review your payment and update your account within 24 hours."}</p>
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-600" />Save this page — you can return anytime to check your status.</p></>
              )}
            </div>
          )}
        </motion.div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="sa-preview-modal">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <p className="font-bold text-sm">{preview.name} — preview</p>
              <button onClick={() => setPreview(null)} className="h-8 w-8 rounded-full bg-muted flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <iframe src={`/api/public/themes/${preview.id}/demo-html`} title="Theme preview" className="flex-1 w-full min-h-[60vh]" />
          </div>
        </div>
      )}
    </div>
  );
}
